import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Badge } from '../../ui/badge';
import { Button } from '../../ui/button';
import { ChevronLeft, ChevronRight, MessageSquare, Clock, CheckCircle, XCircle, X, ExternalLink } from 'lucide-react';
import { getEffectivePlanForClient, getCurrentDateInTimezone, selectDayType } from '@/data/nutritionAssignments';
import type { DayType } from '@/types/nutrition';
import {
  getPendingRequestsCountForClient,
  getClientRequestHistory,
  dismissNutritionRequest,
  type NutritionRequest,
} from '@/data/nutritionRequests';
import { RequestChanges } from './RequestChanges';
import { toast } from 'sonner';
import { getNutritionPdfSignedUrl } from '@/data/nutritionPdfs';
import { InlinePdfViewer } from '../../ui/InlinePdfViewer';
import { reportSwallowed } from '@/lib/sentry';

interface AssignedMealsProps {
  clientId: string;
}

interface Assignment {
  id: string;
  title: string | null;
  notes: string | null;
  start_date: string;
  timezone: string;
  effective_data: DayType[]; // NEW: Direct editor format
  pdf_object_path?: string | null;
  effective_snapshot?: any; // Optional for backward compat
  is_customized: boolean;
  customized_at: string | null;
  client_edit_scope: string;
  created_at: string;
}

// Type for RequestChanges component that requires effective_snapshot
interface _AssignmentForRequest {
  id: string;
  title: string | null;
  effective_snapshot: any;
}

interface _MealEntry {
  food_id: string;
  name: string;
  brand?: string | null;
  portion_id?: string | null;
  portion_label: string;
  portion_grams: number;
  quantity: number;
  total_grams: number;
  kcal: number;
  protein_g: number;
  carbs_g: number;
  fat_g: number;
}

interface DayTypeDisplay {
  label: string;
  meals: any[]; // Meal[] format from DayType
  index: number;
}

export function AssignedMeals({ clientId }: AssignedMealsProps) {
  const [assignment, setAssignment] = useState<Assignment | null>(null);
  const [dayTypes, setDayTypes] = useState<DayTypeDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDayIndex, setSelectedDayIndex] = useState<number>(0);
  const [showRequestModal, setShowRequestModal] = useState(false);
  const [pendingRequestsCount, setPendingRequestsCount] = useState(0);
  const [recentResponse, setRecentResponse] = useState<NutritionRequest | null>(null);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // Track dismissed IDs to prevent re-showing
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());

  // Poll for request status updates
  useEffect(() => {
    const checkRequestStatus = async () => {
      try {
        const requests = await getClientRequestHistory();
        console.log(
          '[AssignedMeals] Fetched requests:',
          requests.map((r) => ({
            id: r.id,
            status: r.status,
            dismissed_at: r.dismissed_at,
            reviewed_at: r.reviewed_at,
          })),
        );

        // Find the most recent reviewed request that hasn't been dismissed
        const reviewed = requests.find(
          (r) => r.status !== 'pending' && !r.dismissed_at && r.reviewed_at && !dismissedIds.has(r.id), // Also check local dismissed set
        );

        console.log('[AssignedMeals] Reviewed notification to show:', reviewed?.id || 'none');

        if (reviewed) {
          setRecentResponse(reviewed);
        } else {
          setRecentResponse(null);
        }
      } catch (error) {
        console.error('[AssignedMeals] Failed to check request status:', error);
        reportSwallowed('AssignedMeals.checkRequestStatus', error);
      }
    };

    // Check immediately
    checkRequestStatus();

    // Poll every 10 seconds
    const interval = setInterval(checkRequestStatus, 10000);

    return () => clearInterval(interval);
  }, [dismissedIds]);

  const handleDismissNotification = async () => {
    if (!recentResponse) return;

    // Optimistically clear immediately and track as dismissed
    const dismissedId = recentResponse.id;
    setRecentResponse(null);
    setDismissedIds((prev) => new Set([...prev, dismissedId]));

    try {
      await dismissNutritionRequest(dismissedId);
      console.log('[AssignedMeals] Notification dismissed successfully:', dismissedId);
      toast.success('Notification dismissed');
    } catch (error) {
      console.error('[AssignedMeals] Failed to dismiss notification:', error);
      toast.error('Failed to dismiss notification');
      // Don't restore on error - user intent was to dismiss
    }
  };

  useEffect(() => {
    const loadAssignment = async () => {
      try {
        setLoading(true);
        setError(null);

        // Get current date in assignment timezone if available
        const todayISO = new Date().toISOString().split('T')[0]; // Default to browser timezone
        const effectivePlan = await getEffectivePlanForClient(clientId, todayISO);

        if (!effectivePlan) {
          setAssignment(null);
          setDayTypes([]);
          return;
        }

        setAssignment(effectivePlan);

        // Extract day types from effective_data (DayType[]) instead of effective_snapshot
        const dayTypesData = (effectivePlan.effective_data || []).map((dayType: DayType, index: number) => ({
          label: dayType.name || `Day ${index + 1}`,
          meals: dayType.meals || [],
          index,
        }));

        setDayTypes(dayTypesData);
        setPdfUrl(null);

        // Set initial selected day (default to first day, or find today's rotation)
        if (dayTypesData.length > 0) {
          // Calculate which day type should be shown today based on rotation
          const todayInAssignmentTimezone = getCurrentDateInTimezone(effectivePlan.timezone);
          const todaysDayType = selectDayType(
            effectivePlan.effective_data || [],
            effectivePlan.start_date,
            todayInAssignmentTimezone,
            effectivePlan.timezone,
          );

          // Find the index of today's day type
          const todayIndex = (effectivePlan.effective_data || []).findIndex(
            (dt: DayType) => dt.id === todaysDayType?.id,
          );
          setSelectedDayIndex(todayIndex >= 0 ? todayIndex : 0);
        }

        console.log('📅 Day types extracted:', {
          totalDayTypes: dayTypesData.length,
          isCustomized: effectivePlan.is_customized,
          editScope: effectivePlan.client_edit_scope,
          dayTypes: dayTypesData.map((dt: DayTypeDisplay) => ({
            index: dt.index,
            label: dt.label,
            mealsCount: dt.meals.length,
            mealNames: dt.meals.map((m: any) => m.name || m.label),
          })),
        });

        // Load pending requests count
        const pendingCount = await getPendingRequestsCountForClient(clientId);
        setPendingRequestsCount(pendingCount);

        if (
          (!effectivePlan.effective_data || effectivePlan.effective_data.length === 0) &&
          effectivePlan.pdf_object_path
        ) {
          setPdfLoading(true);
          setPdfError(null);
          try {
            const signedUrl = await getNutritionPdfSignedUrl(effectivePlan.pdf_object_path);
            setPdfUrl(signedUrl || null);
          } catch (error: any) {
            setPdfUrl(null);
            setPdfError(error?.message || 'Unable to load PDF');
          } finally {
            setPdfLoading(false);
          }
        }
      } catch (err: any) {
        console.error('Failed to load nutrition assignment:', err);
        setError(err?.message || 'Failed to load nutrition assignment');
      } finally {
        setLoading(false);
      }
    };

    if (clientId) {
      loadAssignment();
    }
  }, [clientId]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>My Nutrition Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-3/4"></div>
            <div className="h-4 bg-muted rounded w-1/2"></div>
            <div className="h-4 bg-muted rounded w-2/3"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>My Nutrition Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-red-500">
            <p>Error loading nutrition plan: {error}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!assignment) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>My Nutrition Plan</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-muted-foreground">
            <p>No assigned nutrition plan yet.</p>
            <p className="text-sm mt-2">Your coach will assign a nutrition plan when ready.</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Get selected day type's meals
  const selectedDayType = dayTypes[selectedDayIndex];
  const selectedDayMeals = selectedDayType?.meals || [];

  const showPdfOnly =
    (!assignment.effective_data || assignment.effective_data.length === 0) && !!assignment.pdf_object_path;

  const totalDayNutrition = selectedDayMeals.reduce(
    (dayTotal: { kcal: number; protein_g: number; carbs_g: number; fat_g: number }, meal: any) => {
      const mealTotal = (meal.foods || []).reduce(
        (mealAcc: { kcal: number; protein_g: number; carbs_g: number; fat_g: number }, food: any) => ({
          kcal: mealAcc.kcal + (food.nutrition?.calories || 0),
          protein_g: mealAcc.protein_g + (food.nutrition?.protein || 0),
          carbs_g: mealAcc.carbs_g + (food.nutrition?.carbs || 0),
          fat_g: mealAcc.fat_g + (food.nutrition?.fat || 0),
        }),
        { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 },
      );
      return {
        kcal: dayTotal.kcal + mealTotal.kcal,
        protein_g: dayTotal.protein_g + mealTotal.protein_g,
        carbs_g: dayTotal.carbs_g + mealTotal.carbs_g,
        fat_g: dayTotal.fat_g + mealTotal.fat_g,
      };
    },
    { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 },
  );

  const navigateDay = (direction: 'prev' | 'next') => {
    if (dayTypes.length === 0) return;

    let newIndex;
    if (direction === 'prev') {
      newIndex = selectedDayIndex > 0 ? selectedDayIndex - 1 : dayTypes.length - 1;
    } else {
      newIndex = selectedDayIndex < dayTypes.length - 1 ? selectedDayIndex + 1 : 0;
    }

    setSelectedDayIndex(newIndex);
  };

  return (
    <div className="space-y-6">
      {/* Notification Banner for Coach Response */}
      {recentResponse && (
        <div
          className={`relative flex items-start gap-3 p-4 rounded-lg border ${recentResponse.status === 'approved' ? 'border-green-500 bg-green-500/10' : 'border-red-500 bg-red-500/10'}`}
        >
          <div className="flex-shrink-0 mt-0.5">
            {recentResponse.status === 'approved' ? (
              <CheckCircle className="w-5 h-5 text-green-600" />
            ) : (
              <XCircle className="w-5 h-5 text-red-600" />
            )}
          </div>

          <div className="flex-1 pr-8">
            <div className="flex flex-wrap items-baseline gap-2 mb-1">
              <h4 className="text-sm font-semibold">
                {recentResponse.status === 'approved' ? 'Request Approved!' : 'Request Declined'}
              </h4>
              <span className="text-xs text-muted-foreground">
                {new Date(recentResponse.reviewed_at!).toLocaleDateString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </span>
            </div>

            <p className="text-sm text-foreground">
              {recentResponse.coach_response ||
                (recentResponse.status === 'approved'
                  ? 'Changes have been applied to your plan!'
                  : 'Your request was not approved at this time.')}
            </p>
          </div>

          <Button
            variant="ghost"
            size="sm"
            className="absolute top-2 right-2 h-6 w-6 p-0 hover:bg-muted"
            onClick={handleDismissNotification}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      )}

      <Card>
        <CardHeader className="pb-4">
          <CardTitle className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex min-w-0 flex-wrap items-center gap-2">
              <span className="text-base md:text-lg">{assignment?.title || 'My Nutrition Plan'}</span>
              {assignment?.is_customized && (
                <Badge variant="outline" className="border-blue-500/20 bg-blue-500/10 text-[11px] text-blue-600">
                  Customized
                </Badge>
              )}
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {pendingRequestsCount > 0 && (
                <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                  <Clock className="h-3 w-3" />
                  {pendingRequestsCount} pending
                </span>
              )}
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                onClick={() => setShowRequestModal(true)}
                title="Request changes from your coach — all modifications require approval"
              >
                <MessageSquare className="mr-1 h-3 w-3" />
                Request Changes
              </Button>
            </div>
          </CardTitle>
          {assignment?.notes && <p className="text-sm text-muted-foreground">{assignment.notes}</p>}
        </CardHeader>
        <CardContent>
          {showPdfOnly && (
            <div className="mb-6 rounded-lg border p-4">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">This plan is shared as a PDF document.</p>
                {!!pdfUrl && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="shrink-0 self-start sm:self-auto"
                    onClick={() => window.open(pdfUrl, '_blank', 'noopener,noreferrer')}
                  >
                    <ExternalLink className="w-3 h-3 mr-1" />
                    Open PDF
                  </Button>
                )}
              </div>
              {pdfLoading ? (
                <div className="text-sm text-muted-foreground">Loading PDF...</div>
              ) : pdfUrl ? (
                <div className="max-h-[70vh] overflow-y-auto rounded-md border border-border bg-muted/20 p-2">
                  <InlinePdfViewer url={pdfUrl} className="w-full" />
                </div>
              ) : (
                <div className="space-y-2 text-sm text-muted-foreground">
                  <div>{pdfError || 'Unable to load the PDF right now. Please try again later.'}</div>
                  {assignment.pdf_object_path && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={async () => {
                        try {
                          setPdfLoading(true);
                          setPdfError(null);
                          const signedUrl = await getNutritionPdfSignedUrl(assignment.pdf_object_path!);
                          setPdfUrl(signedUrl);
                        } catch (error: any) {
                          setPdfError(error?.message || 'Unable to load PDF');
                        } finally {
                          setPdfLoading(false);
                        }
                      }}
                    >
                      Retry PDF Load
                    </Button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Day Type Navigation — chevrons + segmented pills on one row */}
          {!showPdfOnly && dayTypes.length > 0 && (
            <div className="mb-4 flex items-center gap-2">
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => navigateDay('prev')}
                aria-label="Previous day type"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <div className="flex flex-1 justify-center gap-1.5 overflow-x-auto">
                {dayTypes.map((dayType) => {
                  const isSelected = dayType.index === selectedDayIndex;
                  const mealsCount = dayType.meals.length;

                  return (
                    <button
                      key={dayType.index}
                      onClick={() => setSelectedDayIndex(dayType.index)}
                      className={`shrink-0 rounded-full px-3 py-1.5 text-xs font-medium transition-colors ${
                        isSelected
                          ? 'bg-primary text-primary-foreground'
                          : 'bg-muted text-muted-foreground hover:bg-muted/80 hover:text-foreground'
                      }`}
                    >
                      {dayType.label}
                      {mealsCount > 0 && <span className="ml-1 opacity-70">· {mealsCount}</span>}
                    </button>
                  );
                })}
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 shrink-0"
                onClick={() => navigateDay('next')}
                aria-label="Next day type"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}

          {/* Daily totals — compact stat strip */}
          {!showPdfOnly && (
            <div className="grid grid-cols-4 divide-x divide-border rounded-lg border border-border bg-background/60">
              {[
                { value: Math.round(totalDayNutrition.kcal), label: 'Calories' },
                { value: `${Math.round(totalDayNutrition.protein_g)}g`, label: 'Protein' },
                { value: `${Math.round(totalDayNutrition.carbs_g)}g`, label: 'Carbs' },
                { value: `${Math.round(totalDayNutrition.fat_g)}g`, label: 'Fat' },
              ].map((s) => (
                <div key={s.label} className="px-2 py-2.5 text-center md:py-3">
                  <p className="text-base font-semibold leading-6 tabular-nums text-foreground md:text-lg">{s.value}</p>
                  <p className="text-[10px] uppercase tracking-wide text-muted-foreground">{s.label}</p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Meals */}
      {!showPdfOnly && (
        <div className="space-y-4">
          {selectedDayMeals.map((meal, mealIndex) => {
            const mealTotals = (meal.foods || []).reduce(
              (acc: { kcal: number; protein_g: number; carbs_g: number; fat_g: number }, food: any) => ({
                kcal: acc.kcal + (food.nutrition?.calories || 0),
                protein_g: acc.protein_g + (food.nutrition?.protein || 0),
                carbs_g: acc.carbs_g + (food.nutrition?.carbs || 0),
                fat_g: acc.fat_g + (food.nutrition?.fat || 0),
              }),
              { kcal: 0, protein_g: 0, carbs_g: 0, fat_g: 0 },
            );

            return (
              <Card key={mealIndex} className="overflow-hidden border-border bg-card py-0">
                <div className="flex items-center justify-between gap-3 border-b border-border bg-muted/40 px-4 py-2.5">
                  <h3 className="min-w-0 truncate text-sm font-semibold text-foreground">{meal.name || meal.label}</h3>
                  <p className="shrink-0 text-xs text-muted-foreground">
                    <span className="font-semibold text-foreground">{Math.round(mealTotals.kcal)}</span> cal
                    <span className="ml-2 hidden sm:inline">
                      P {Math.round(mealTotals.protein_g)} · C {Math.round(mealTotals.carbs_g)} · F{' '}
                      {Math.round(mealTotals.fat_g)}
                    </span>
                  </p>
                </div>
                <div className="divide-y divide-border">
                  {(meal.foods || []).map((food: any, foodIndex: number) => (
                    <div key={foodIndex} className="flex items-center justify-between gap-3 px-4 py-2.5">
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-foreground">
                          {food.foodItem?.name || food.name}
                        </p>
                        <p className="truncate text-xs text-muted-foreground">
                          {food.quantity} × {food.unit || food.portion_label || 'serving'}
                          {food.basePerGram?.calories > 0
                            ? ` (${Math.round((food.nutrition?.calories || 0) / food.basePerGram.calories)}g)`
                            : null}
                          {food.foodItem?.brand ? ` · ${food.foodItem.brand}` : ''}
                        </p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-medium tabular-nums text-foreground">
                          {Math.round(food.nutrition?.calories || 0)} cal
                        </p>
                        <p className="text-[11px] tabular-nums text-muted-foreground">
                          P {Math.round(food.nutrition?.protein || 0)} · C {Math.round(food.nutrition?.carbs || 0)} · F{' '}
                          {Math.round(food.nutrition?.fat || 0)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {!showPdfOnly && selectedDayMeals.length === 0 && dayTypes.length > 0 && (
        <Card>
          <CardContent className="text-center py-8 text-muted-foreground">
            <p>No meals planned for {selectedDayType?.label || 'this day type'}.</p>
          </CardContent>
        </Card>
      )}

      {/* Request Changes Modal */}
      {assignment && (
        <RequestChanges
          open={showRequestModal}
          onOpenChange={setShowRequestModal}
          assignment={{
            id: assignment.id,
            title: assignment.title,
            effective_snapshot: assignment.effective_snapshot || assignment.effective_data,
          }}
          onRequestSubmitted={async () => {
            // Reload pending requests count
            const pendingCount = await getPendingRequestsCountForClient(clientId);
            setPendingRequestsCount(pendingCount);
          }}
        />
      )}
    </div>
  );
}
